import { Node, mergeAttributes } from "@tiptap/core";
import Suggestion, { type SuggestionProps as TipTapSuggestionProps } from "@tiptap/suggestion";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import type { Transaction, EditorState } from "@tiptap/pm/state";
import { PluginKey } from "@tiptap/pm/state";

// Unique plugin key for mention suggestions
const MentionPluginKey = new PluginKey("mention-suggestion");

export interface MentionNodeAttrs {
  id: string;
  label: string;
}

// Extended props with type discriminator
export interface MentionSuggestionProps extends Omit<TipTapSuggestionProps, 'editor'> {
  type: "mention" | "song";
}

export interface MentionSuggestionHandlers {
  onStart?: (props: MentionSuggestionProps) => void;
  onUpdate?: (props: MentionSuggestionProps) => void;
  onExit?: () => void;
  onKeyDown?: (props: { event: KeyboardEvent }) => boolean;
}

export interface MentionOptions {
  suggestion?: MentionSuggestionHandlers;
}

export const MentionExtension = Node.create<MentionOptions>({
  name: "mention",

  group: "inline",

  inline: true,

  selectable: false,

  atom: true,

  addOptions() {
    return {
      suggestion: undefined,
    };
  },

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-id"),
        renderHTML: (attributes: Record<string, unknown>) => ({
          "data-id": attributes.id,
        }),
      },
      label: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-label"),
        renderHTML: (attributes: Record<string, unknown>) => ({
          "data-label": attributes.label,
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: `span[data-type="${this.name}"]`,
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }: { node: ProseMirrorNode; HTMLAttributes: Record<string, unknown> }) {
    return [
      "span",
      mergeAttributes(
        { "data-type": this.name, class: "mention" },
        HTMLAttributes
      ),
      `@${node.attrs.label}`,
    ];
  },

  renderText({ node }: { node: ProseMirrorNode }) {
    return `@${node.attrs.label}`;
  },

  addKeyboardShortcuts() {
    return {
      Backspace: () =>
        this.editor.commands.command(({ tr, state }: { tr: Transaction; state: EditorState }) => {
          let isMention = false;
          const { selection } = state;
          const { empty, anchor } = selection;

          if (!empty) {
            return false;
          }

          state.doc.nodesBetween(anchor - 1, anchor, (node: ProseMirrorNode, pos: number) => {
            if (node.type.name === this.name) {
              isMention = true;
              tr.insertText("", pos, pos + node.nodeSize);
              return false;
            }
          });

          return isMention;
        }),
    };
  },

  addProseMirrorPlugins() {
    const handlers = this.options.suggestion;

    return [
      Suggestion({
        editor: this.editor,
        pluginKey: MentionPluginKey,
        char: "@",
        command: () => {
          // Command is handled externally via suggestion handlers
        },
        allow: ({ state, range }) => {
          const $from = state.doc.resolve(range.from);
          const type = state.schema.nodes[this.name];
          const allow = !!$from.parent.type.contentMatch.matchType(type);
          return allow;
        },
        render: () => {
          return {
            onStart: (props: TipTapSuggestionProps) => {
              handlers?.onStart?.({ ...props, type: "mention" });
            },
            onUpdate: (props: TipTapSuggestionProps) => {
              handlers?.onUpdate?.({ ...props, type: "mention" });
            },
            onExit: () => {
              handlers?.onExit?.();
            },
            onKeyDown: (props: { event: KeyboardEvent }) => {
              return handlers?.onKeyDown?.(props) ?? false;
            },
          };
        },
      }),
    ];
  },
});
